/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { ChangeEvent } from 'react';

const typeValidator = (file: File | null): boolean => {
  if (!file) return false;
  const accept = [
    'image',
    'pdf',
    'text/plain',
    'msword',
    'excel',
    'powerpoint',
    'officedocument',
    'opendocument',
    'xml',
    'fnm',
    'csv',
    'm4a',
    'wav',
    'mp3',
    'aac',
    'flac',
    'wma',
    'aiff',
  ];
  const acceptNoType = ['fnm'];
  if (file?.type && accept.some((x) => file.type.includes(x))) {
    return true;
  } else {
    // If file doesn't have a type, check file ending
    if (file?.name && file?.type === '') {
      let fileParts = file.name.split('.');
      let ending = fileParts[fileParts.length - 1].toLowerCase();
      if (acceptNoType.includes(ending)) {
        return true;
      }
    }
    return false;
  }
};

const handleFile = (event?: ClipboardEvent | DragEvent | ChangeEvent): string | File[] => {
  let files: File[] = [];
  let error: string = '';

  if (event instanceof ClipboardEvent) {
    if (!event?.clipboardData || !event?.clipboardData?.items) return [];
    if (event?.clipboardData?.getData('text/plain')) return [];
    for (let i = 0; i < event.clipboardData.items.length; i++) {
      if (!error) {
        let file = event.clipboardData.items[i].getAsFile();

        if (file && typeValidator(file)) {
          if (file.size < 30000000) {
            files.push(file);
          } else {
            error = 'documentSizeLimit';
          }
        } else {
          error = 'documentTypeError';
        }
      }
    }
  } else if (event instanceof DragEvent) {
    if (!event?.dataTransfer || (!event?.dataTransfer?.items && !event?.dataTransfer?.files)) return [];
    if (event?.dataTransfer?.items) {
      [...event.dataTransfer.items].forEach((item, i) => {
        if (!error) {
          const file = item.getAsFile();
          if (file && typeValidator(file)) {
            if (file.size < 30000000) {
              files.push(file);
            } else {
              error = 'documentSizeLimit';
            }
          } else if (item.type !== 'application/json') {
            error = 'documentTypeError';
          }
        }
      });
    } else if (event?.dataTransfer?.files) {
      [...event.dataTransfer.files].forEach((file, i) => {
        if (!error) {
          if (file && typeValidator(file)) {
            if (file.size < 30000000) {
              files.push(file);
            } else {
              error = 'documentSizeLimit';
            }
          } else {
            error = 'documentTypeError';
          }
        }
      });
    }
  } else {
    event?.preventDefault();
    let target = event?.target as HTMLInputElement;
    if (!target || !target?.files || target?.files?.length === 0) return [];
    for (let file of target.files) {
      if (!error) {
        if (file && typeValidator(file)) {
          if (file.size < 30000000) {
            files.push(file);
          } else {
            error = 'documentSizeLimit';
          }
        } else {
          error = 'documentTypeError';
        }
      }
    }
    target.value = '';
  }

  if (error.length > 0) {
    return error;
  }

  files.forEach((x: File) => {
    let newName = x.name.replace(/[~` !@#$%^&*()+={}[\];:'"<>,/\\?-]/g, '');
    Object.defineProperty(x, 'name', {
      writable: true,
      value: newName,
    });
  });

  return files;
};

export default handleFile;
